import type { VisitorLanguage } from '../visitor/texts.js';

/**
 * The booth kiosk's own words (brief section 7.5), in the six languages of
 * Hand the tablet; the form's fields and the consent are Hand the tablet's
 * (visitor/texts.ts). Written by Claude, to be reviewed by native speakers
 * later without blocking.
 */
export interface KioskTexts {
  headline: string;
  homeSub: string;
  watch: string;
  read: string;
  videos(n: number): string;
  documents(n: number): string;
  receive: string;
  chooseWhat: string;
  selected(n: number): string;
  back: string;
  videosTitle: string;
  documentsTitle: string;
  add: string;
  added: string;
  receiveSub: string;
  whatToReceive: string;
  selectAll: string;
  video: string;
  document: string;
  workEmail: string;
  pickOne: string;
  fillIn: string;
  thanks: string;
  thanksSub: string;
  close: string;
  opening: string;
  cannotShow: string;
}

const count = (one: string, many: string) => (n: number) => `${n} ${n === 1 ? one : many}`;

export const KIOSK_TEXTS: Record<VisitorLanguage, KioskTexts> = {
  en: {
    headline: 'Discover Waste Robotics',
    homeSub: 'Touch a tile to explore. Pick what interests you and receive it by email.',
    watch: 'Watch',
    read: 'Read',
    videos: count('video', 'videos'),
    documents: count('document', 'documents'),
    receive: 'Receive info',
    chooseWhat: 'Choose what you want to receive',
    selected: (n) => `${n} selected`,
    back: 'Back',
    videosTitle: 'Videos',
    documentsTitle: 'Documents',
    add: '+ Add to my info',
    added: '✓ Added to my info',
    receiveSub: 'Choose what you want to receive, then leave your contact details.',
    whatToReceive: 'What do you want to receive?',
    selectAll: 'Select all',
    video: 'Video',
    document: 'Document',
    workEmail: 'Work email',
    pickOne: 'Choose at least one item to receive.',
    fillIn: 'Please fill in your name, company and a valid email.',
    thanks: 'Check your inbox',
    thanksSub: 'It is on its way. Thanks for visiting Waste Robotics.',
    close: 'Close',
    opening: 'Opening',
    cannotShow: 'This document cannot be shown here.',
  },
  fr: {
    headline: 'Découvrez Waste Robotics',
    homeSub:
      'Touchez une tuile pour explorer. Choisissez ce qui vous intéresse et recevez-le par courriel.',
    watch: 'Regarder',
    read: 'Lire',
    videos: count('vidéo', 'vidéos'),
    documents: count('document', 'documents'),
    receive: 'Recevoir l’information',
    chooseWhat: 'Choisissez ce que vous voulez recevoir',
    selected: (n) => `${n} choisi${n === 1 ? '' : 's'}`,
    back: 'Retour',
    videosTitle: 'Vidéos',
    documentsTitle: 'Documents',
    add: '+ Ajouter à mon envoi',
    added: '✓ Ajouté à mon envoi',
    receiveSub: 'Choisissez ce que vous voulez recevoir, puis laissez vos coordonnées.',
    whatToReceive: 'Que voulez-vous recevoir?',
    selectAll: 'Tout choisir',
    video: 'Vidéo',
    document: 'Document',
    workEmail: 'Courriel professionnel',
    pickOne: 'Choisissez au moins un élément à recevoir.',
    fillIn: 'Indiquez votre nom, votre entreprise et un courriel valide.',
    thanks: 'Consultez vos courriels',
    thanksSub: 'C’est en route. Merci de votre visite chez Waste Robotics.',
    close: 'Fermer',
    opening: 'Ouverture',
    cannotShow: 'Ce document ne peut pas être affiché ici.',
  },
  de: {
    headline: 'Entdecken Sie Waste Robotics',
    homeSub:
      'Tippen Sie auf eine Kachel. Wählen Sie, was Sie interessiert, und erhalten Sie es per E-Mail.',
    watch: 'Ansehen',
    read: 'Lesen',
    videos: count('Video', 'Videos'),
    documents: count('Dokument', 'Dokumente'),
    receive: 'Infos erhalten',
    chooseWhat: 'Wählen Sie, was Sie erhalten möchten',
    selected: (n) => `${n} ausgewählt`,
    back: 'Zurück',
    videosTitle: 'Videos',
    documentsTitle: 'Dokumente',
    add: '+ Zu meinen Infos',
    added: '✓ In meinen Infos',
    receiveSub: 'Wählen Sie, was Sie erhalten möchten, und hinterlassen Sie Ihre Kontaktdaten.',
    whatToReceive: 'Was möchten Sie erhalten?',
    selectAll: 'Alle wählen',
    video: 'Video',
    document: 'Dokument',
    workEmail: 'Geschäftliche E-Mail',
    pickOne: 'Wählen Sie mindestens ein Element.',
    fillIn: 'Bitte geben Sie Ihren Namen, Ihr Unternehmen und eine gültige E-Mail an.',
    thanks: 'Sehen Sie in Ihr Postfach',
    thanksSub: 'Es ist unterwegs. Danke für Ihren Besuch bei Waste Robotics.',
    close: 'Schließen',
    opening: 'Wird geöffnet',
    cannotShow: 'Dieses Dokument kann hier nicht angezeigt werden.',
  },
  nl: {
    headline: 'Ontdek Waste Robotics',
    homeSub: 'Tik op een tegel. Kies wat u interesseert en ontvang het per e-mail.',
    watch: 'Bekijken',
    read: 'Lezen',
    videos: count('video', "video's"),
    documents: count('document', 'documenten'),
    receive: 'Info ontvangen',
    chooseWhat: 'Kies wat u wilt ontvangen',
    selected: (n) => `${n} gekozen`,
    back: 'Terug',
    videosTitle: "Video's",
    documentsTitle: 'Documenten',
    add: '+ Aan mijn info toevoegen',
    added: '✓ In mijn info',
    receiveSub: 'Kies wat u wilt ontvangen en laat uw gegevens achter.',
    whatToReceive: 'Wat wilt u ontvangen?',
    selectAll: 'Alles kiezen',
    video: 'Video',
    document: 'Document',
    workEmail: 'Zakelijk e-mailadres',
    pickOne: 'Kies minstens één item.',
    fillIn: 'Vul uw naam, bedrijf en een geldig e-mailadres in.',
    thanks: 'Kijk in uw inbox',
    thanksSub: 'Het is onderweg. Bedankt voor uw bezoek aan Waste Robotics.',
    close: 'Sluiten',
    opening: 'Openen',
    cannotShow: 'Dit document kan hier niet worden getoond.',
  },
  it: {
    headline: 'Scoprite Waste Robotics',
    homeSub: 'Toccate una casella. Scegliete cosa vi interessa e ricevetelo via email.',
    watch: 'Guarda',
    read: 'Leggi',
    videos: count('video', 'video'),
    documents: count('documento', 'documenti'),
    receive: 'Ricevi le informazioni',
    chooseWhat: 'Scegliete cosa volete ricevere',
    selected: (n) => `${n} scelti`,
    back: 'Indietro',
    videosTitle: 'Video',
    documentsTitle: 'Documenti',
    add: '+ Aggiungi alle mie info',
    added: '✓ Nelle mie info',
    receiveSub: 'Scegliete cosa volete ricevere, poi lasciate i vostri recapiti.',
    whatToReceive: 'Cosa volete ricevere?',
    selectAll: 'Scegli tutto',
    video: 'Video',
    document: 'Documento',
    workEmail: 'Email di lavoro',
    pickOne: 'Scegliete almeno un elemento.',
    fillIn: 'Indicate nome, azienda e un’email valida.',
    thanks: 'Controllate la vostra casella',
    thanksSub: 'È in arrivo. Grazie per la visita a Waste Robotics.',
    close: 'Chiudi',
    opening: 'Apertura',
    cannotShow: 'Questo documento non può essere mostrato qui.',
  },
  es: {
    headline: 'Descubra Waste Robotics',
    homeSub: 'Toque una casilla. Elija lo que le interesa y recíbalo por correo electrónico.',
    watch: 'Ver',
    read: 'Leer',
    videos: count('vídeo', 'vídeos'),
    documents: count('documento', 'documentos'),
    receive: 'Recibir información',
    chooseWhat: 'Elija lo que quiere recibir',
    selected: (n) => `${n} elegidos`,
    back: 'Volver',
    videosTitle: 'Vídeos',
    documentsTitle: 'Documentos',
    add: '+ Añadir a mi información',
    added: '✓ En mi información',
    receiveSub: 'Elija lo que quiere recibir y deje sus datos de contacto.',
    whatToReceive: '¿Qué quiere recibir?',
    selectAll: 'Elegir todo',
    video: 'Vídeo',
    document: 'Documento',
    workEmail: 'Correo electrónico profesional',
    pickOne: 'Elija al menos un elemento.',
    fillIn: 'Indique su nombre, empresa y un correo electrónico válido.',
    thanks: 'Revise su correo',
    thanksSub: 'Está en camino. Gracias por visitar Waste Robotics.',
    close: 'Cerrar',
    opening: 'Abriendo',
    cannotShow: 'Este documento no se puede mostrar aquí.',
  },
};

/** The kiosk package's email and page (brief section 16.2): in the kiosk's language when available, else English. */
export const KIOSK_PACKAGE_TEXTS = {
  en: {
    title: 'Your visit to Waste Robotics',
    message: (event: string) =>
      `Thank you for visiting our booth${event ? ` at ${event}` : ''}. Here is what you asked for.`,
    subject: (event: string) => `Your visit to Waste Robotics${event ? ` at ${event}` : ''}`,
    hello: (name: string) => `Hello ${name},`,
    open: 'Open your page (with this email address):',
    items: 'What you asked for:',
    available: (date: string) => `Available until ${date}.`,
  },
  fr: {
    title: 'Votre visite chez Waste Robotics',
    message: (event: string) =>
      `Merci de votre visite à notre kiosque${event ? ` à ${event}` : ''}. Voici ce que vous avez demandé.`,
    subject: (event: string) => `Votre visite chez Waste Robotics${event ? ` à ${event}` : ''}`,
    hello: (name: string) => `Bonjour ${name},`,
    open: 'Ouvrez votre page (avec cette adresse courriel) :',
    items: 'Ce que vous avez demandé :',
    available: (date: string) => `Disponible jusqu’au ${date}.`,
  },
} as const;
